import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import { enumReadSchema } from "../../../../shared/core/enum-read.schema";
import {
  VibeApprovalDecisionKindReadSchema,
  VibeApprovalDecisionKindSchema,
  VibeApprovalDecisionNoteSchema
} from "./approvals.schemas";
import { VibeApprovalRequestStatusReadSchema } from "./status-read.schemas";

/**
 * Contracts for the capacity-approval flow — the sibling of the deploy-approval
 * flow (`approvals.schemas.ts`) keyed to a tenant + node-size knob rather than a
 * deployment. Reuses the deploy-approval status / decision-kind / note schemas
 * (they are semantically generic) so the two flows never drift apart.
 */

/**
 * Which node-size knob a capacity grow targets, from the generated `DbEnum`
 * tuple. That module is plain string tuples and carries none of Prisma's runtime
 * into the frontend bundle.
 */
export const VibeCapacityApprovalSubjectSchema = z.enum(
  DbEnum.VIBE_CAPACITY_APPROVAL_SUBJECT_VALUES
);

/** The subject as a reader parses it: a subject this build does not list reads as unlisted. */
export const VibeCapacityApprovalSubjectReadSchema = enumReadSchema(
  DbEnum.VIBE_CAPACITY_APPROVAL_SUBJECT_VALUES
);
export type VibeCapacityApprovalSubjectValue = z.infer<typeof VibeCapacityApprovalSubjectSchema>;

/**
 * The provisioner's default AND grow-only floor for a workload node's root
 * volume (GiB). A tenant with no approved grow has `clientRootVolumeGb` /
 * `gitHostRootVolumeGb` null on its cluster row, and the provisioner boots nodes
 * at this size.
 *
 * ⚠️ Mirrored as `DEFAULT_WORKLOAD_ROOT_VOLUME_GB` in
 * `apps/vibe-provisioner/stacks/nomad/workload-root-volume.ts`, which cannot import this package (the
 * Pulumi app carries no `@nexus/types` dependency — same cross-reference-by-
 * comment convention as `VIBE_EDGE_SLOT_SHUTDOWN_DELAY_SECONDS` and
 * `VIBE_FORGEJO_EDGE_HOST_PORT`). Change one, change the other: this value is
 * what the control plane reports as a tenant's CURRENT size when the column is
 * null, so a drift would surface a grow request against a size the nodes do not
 * actually have.
 */
export const VIBE_DEFAULT_ROOT_VOLUME_GB = 50;

/**
 * How much bigger a system-raised capacity request SUGGESTS growing by (GiB).
 *
 * This is a suggestion, not a policy: the approver can edit the target when they
 * decide (`RecordVibeCapacityApprovalDecisionBodySchema.targetGb`) and their
 * value wins. A fixed step keeps the cost delta of an approval predictable and
 * matches the 50 GiB floor the fleet already provisions at; the docker-image
 * prune timer keeps requests rare enough that a linear step does not lag real
 * growth.
 */
export const VIBE_CAPACITY_GROW_STEP_GB = 50;

/**
 * Upper bound for an approved root volume (GiB) — the EBS gp3 per-volume
 * ceiling. Bounds the approver's override so a typo cannot request a volume AWS
 * would reject at converge time (which would leave the request APPROVED and the
 * converge failing).
 */
export const VIBE_MAX_ROOT_VOLUME_GB = 16384;

/**
 * The target size a system-raised capacity request suggests for a subject
 * currently at `currentGb`. Grow-only by construction (the step is positive), so
 * the result always satisfies the request entity's `targetGb > currentGb`
 * invariant. Clamped to the gp3 ceiling so a tenant already at the maximum
 * yields no runaway value.
 */
export function suggestVibeCapacityTargetGb(currentGb: number): number {
  return Math.min(currentGb + VIBE_CAPACITY_GROW_STEP_GB, VIBE_MAX_ROOT_VOLUME_GB);
}

export const VibeCapacityApprovalDecisionSchema = z.object({
  id: z.string().uuid(),
  vibeCapacityApprovalRequestId: z.string().uuid(),
  organizationId: z.string(),
  decision: VibeApprovalDecisionKindReadSchema,
  /// Nullable — the FK uses SET NULL on User delete so the decision row survives
  /// the reviewer's account being removed (audit-evidence invariant). Live rows
  /// always carry a non-null value; the null state is exclusively historical.
  decidedByUserId: z.string().nullable(),
  note: z.string().nullable(),
  decidedAt: z.coerce.date()
});
export type VibeCapacityApprovalDecisionDto = z.infer<typeof VibeCapacityApprovalDecisionSchema>;

export const VibeCapacityApprovalRequestSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string(),
  subject: VibeCapacityApprovalSubjectReadSchema,
  /// Root size (GiB) observed at request time, and the requested target. The
  /// request is grow-only (targetGb > currentGb), enforced at creation.
  currentGb: z.number().int().positive(),
  targetGb: z.number().int().positive(),
  status: VibeApprovalRequestStatusReadSchema,
  requiredApprovals: z.number().int().positive(),
  expiresAt: z.coerce.date(),
  decidedAt: z.coerce.date().nullable(),
  /// Who requested the grow. Null when the system threshold detector raised it
  /// (the common path); set when a user requested manually.
  requestedByUserId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
export type VibeCapacityApprovalRequestDto = z.infer<typeof VibeCapacityApprovalRequestSchema>;

// ----------------------------------------------------------------------------
// List the pending capacity approval requests in an org (reviewer queue)
// ----------------------------------------------------------------------------

/**
 * A pending capacity request as the reviewer queue sees it: the request plus
 * the disk pressure that raised it.
 *
 * `usedPercent` / `thresholdPercent` are not columns on the request — they are
 * read back off the `CAPACITY_REQUESTED` audit row, the only place the (transient)
 * Nomad node stats behind them are persisted. Both are nullable: a request whose
 * audit row is missing still belongs in the queue, it just renders without the
 * pressure line. The reviewer needs them because a grow request is an ask to
 * spend more money and the usage figure is the entire justification.
 */
export const VibeCapacityApprovalRequestListItemSchema = VibeCapacityApprovalRequestSchema.extend({
  usedPercent: z.number().nullable(),
  thresholdPercent: z.number().nullable()
});

export type VibeCapacityApprovalRequestListItemDto = z.infer<
  typeof VibeCapacityApprovalRequestListItemSchema
>;

export const ListPendingVibeCapacityApprovalsResponseSchema = z.object({
  requests: z.array(VibeCapacityApprovalRequestListItemSchema)
});

// ----------------------------------------------------------------------------
// Get a capacity approval request (with its decisions)
// ----------------------------------------------------------------------------

export const GetVibeCapacityApprovalRequestPathVarsSchema = z.object({
  requestId: z.string().uuid()
});

export const GetVibeCapacityApprovalRequestResponseSchema = z.object({
  request: VibeCapacityApprovalRequestSchema,
  decisions: z.array(VibeCapacityApprovalDecisionSchema)
});

// ----------------------------------------------------------------------------
// Record a decision on a capacity approval request
// ----------------------------------------------------------------------------

export const RecordVibeCapacityApprovalDecisionPathVarsSchema = z.object({
  requestId: z.string().uuid()
});

export const RecordVibeCapacityApprovalDecisionBodySchema = z.object({
  decision: VibeApprovalDecisionKindSchema,
  note: VibeApprovalDecisionNoteSchema.optional(),
  /// The approver's override of the requested target size (GiB) — the reviewer
  /// has the last word on how big the grow is, with the request's `targetGb`
  /// serving only as the suggested default the UI pre-fills.
  ///
  /// Omitted → the request's suggested `targetGb` applies unchanged. Ignored on
  /// REJECT (no size is written on a reject). On a decisive APPROVE the override
  /// becomes the size stamped onto the tenant cluster AND is written back onto
  /// the request row, so an APPROVED request always reports the size that was
  /// actually approved (the original suggestion stays recoverable from the
  /// `CAPACITY_REQUESTED` audit row).
  ///
  /// Grow-only: the use case refuses a value at or below the request's
  /// `currentGb` — EBS volumes cannot shrink, so a smaller target would fail the
  /// converge rather than take effect.
  targetGb: z.number().int().positive().max(VIBE_MAX_ROOT_VOLUME_GB).optional()
});

export const RecordVibeCapacityApprovalDecisionResponseSchema = z.object({
  request: VibeCapacityApprovalRequestSchema,
  decision: VibeCapacityApprovalDecisionSchema
});
